import type { OrderReceiptSummary } from '@/types/payment.types';

interface OrderReceiptCardProps {
  receipt: OrderReceiptSummary;
  onPay: () => void;
  isPaying?: boolean;
}

/**
 * Order summary + total + Pay button. The Pay button here is desktop-only
 * (d-lg-flex) — on mobile/tablet the same action is offered via the page's
 * StickyFooter so it stays reachable with one thumb.
 */
export function OrderReceiptCard({ receipt, onPay, isPaying }: OrderReceiptCardProps) {
  return (
    <section className="bg-surface-lowest rounded-4 shadow-kiosk-md p-3 p-lg-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <span className="text-label-md text-on-surface-variant">Receipt Preview</span>
        <span className="badge bg-surface-container text-on-surface-variant fw-medium">Order #{receipt.orderId}</span>
      </div>

      <div className="d-flex align-items-start gap-3 pb-3 mb-3 border-bottom">
        <div
          className="rounded-3 bg-surface-container d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 48, height: 60 }}
        >
          <i className="bi bi-file-earmark-pdf text-primary" style={{ fontSize: 22 }} />
        </div>
        <div className="flex-grow-1 min-w-0">
          <h4
            className="text-label-lg text-on-surface mb-1"
            title={receipt.fileName}
          >
            {receipt.fileName}
          </h4>
          <div className="d-flex flex-wrap gap-1">
            <span className="badge bg-surface-container text-on-surface-variant fw-medium">{receipt.colorMode}</span>
            <span className="badge bg-surface-container text-on-surface-variant fw-medium">{receipt.paperSize}</span>
            <span className="badge bg-surface-container text-on-surface-variant fw-medium">{receipt.duplexMode}</span>
            <span className="badge bg-surface-container text-on-surface-variant fw-medium">
              {receipt.copies} {receipt.copies > 1 ? 'Copies' : 'Copy'} ({receipt.totalImpressions} pages)
            </span>
          </div>
        </div>
      </div>

      <div className="d-flex flex-column gap-2 pb-3 mb-3 border-bottom">
        {receipt.lineItems.map((item) => (
          <div key={item.label} className="d-flex align-items-center justify-content-between gap-2">
            <span
              className={[
                'text-body-sm',
                item.isDiscount ? 'text-success d-flex align-items-center gap-1' : 'text-on-surface-variant',
              ].join(' ')}
            >
              {item.isDiscount && <i className="bi bi-tag-fill" />}
              {item.label}
              {item.sublabel && <span className="text-on-surface-variant"> · {item.sublabel}</span>}
            </span>
            <span className={`text-label-sm text-nowrap ${item.isDiscount ? 'text-success' : 'text-on-surface'}`}>
              {item.isFree ? 'FREE' : `${item.isDiscount ? '-' : ''}₹${Math.abs(item.amount).toFixed(2)}`}
            </span>
          </div>
        ))}
      </div>

      <div className="bg-surface-low rounded-3 p-3 d-flex align-items-center justify-content-between mb-3">
        <div>
          <span className="text-label-sm text-on-surface-variant d-block">TOTAL PAYABLE</span>
          <span className="text-body-sm text-on-surface-variant">Inclusive of all local GST</span>
        </div>
        <span className="text-headline-sm text-on-surface fw-bold">₹{receipt.totalDue.toFixed(2)}</span>
      </div>

      <button
        type="button"
        onClick={onPay}
        disabled={isPaying}
        className="btn btn-primary w-100 tap-target active-press d-none d-lg-flex align-items-center justify-content-center gap-2 text-label-lg"
        style={{ height: 52 }}
      >
        {isPaying ? (
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        ) : (
          <i className="bi bi-lock-fill" />
        )}
        <span>Pay ₹{receipt.totalDue.toFixed(2)}</span>
      </button>
    </section>
  );
}
